/**
 * Module 3 — LBO panel. Edit assumptions → run the Python LBO engine → MOIC/IRR, value-creation
 * attribution, and the operating/debt schedule. Entry EBITDA defaults to QofE Defensible EBITDA.
 * Lock makes the model the immutable post-close budget (Module 6 measures variance against it).
 *
 * Phase 7 UI affordances:
 *  • Entry-EBITDA confidence badge (reported / adjusted / defensible / auto)
 *  • Payer-concentration warning banner gating the run
 *  • ASC 842 operating-lease capitalization toggle
 *  • Physician rollover equity S&U field
 */

import React, { useEffect, useState } from 'react';
import { lboApi, exportApi, qofeApi, dealsApi } from '../services/api';

const ACCENT = '#161f4a';
const WARN = '#b45309';
const CRIT = '#b91c1c';
const num = (s) => (s == null ? '—' : Number(s).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const money = (s) => (s == null ? '—' : Number(s).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const pctv = (s) => (s == null ? '—' : `${(Number(s) * 100).toFixed(1)}%`);

const FIELDS = [
  ['entry_multiple', 'Entry ×', 6], ['exit_multiple', 'Exit ×', 6], ['hold_years', 'Hold (yrs)', 5],
  ['leverage_x', 'Leverage ×', 3], ['interest_rate', 'Interest', 0.09], ['ebitda_growth', 'EBITDA growth', 0.05],
  ['fees_pct', 'Fees %EV', 0.02], ['capex_pct', 'Capex %EBITDA', 0.03], ['tax_rate', 'Tax', 0.25],
  ['mandatory_amort_pct', 'Mand. amort', 0.05], ['cash_sweep_pct', 'Cash sweep', 0.75],
];

function badgeForSource(source, bridge) {
  if (!bridge) return { text: 'No QofE — run classification first', color: '#64748b' };
  if (source === 'qofe_defensible') return { text: 'Defensible EBITDA (auto)', color: '#047857' };
  if (source === 'manual') return { text: 'Manual override', color: '#0369a1' };
  return { text: 'Auto', color: '#64748b' };
}

function payerBanner(flags) {
  if (!flags || !flags.length) return null;
  const payerFlags = flags.filter(f =>
    (f.check_type || '').toLowerCase() === 'payer_concentration' ||
    (f.title || '').toLowerCase().includes('payer') ||
    (f.title || '').toLowerCase().includes('medicaid')
  );
  if (!payerFlags.length) return null;
  const worst = payerFlags.some(f => f.severity === 'CRITICAL') ? 'CRITICAL' :
    payerFlags.some(f => f.severity === 'HIGH') ? 'HIGH' : 'MEDIUM';
  const color = worst === 'CRITICAL' ? CRIT : worst === 'HIGH' ? WARN : '#64748b';
  const titles = payerFlags.map(f => f.title).join('; ');
  return { severity: worst, text: `Payer risk: ${titles}`, color };
}

export default function LBOPanel({ dealId }) {
  const [assumptions, setAssumptions] = useState(Object.fromEntries(FIELDS.map(([k, , d]) => [k, d])));
  const [entryEbitda, setEntryEbitda] = useState('');
  const [operatingLeasePv, setOperatingLeasePv] = useState('');
  const [asc842Enabled, setAsc842Enabled] = useState(false);
  const [physicianRollover, setPhysicianRollover] = useState('');
  const [model, setModel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [deal, setDeal] = useState(null);
  const [qofe, setQofe] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [dRes, qRes] = await Promise.all([
          dealsApi.get(dealId).catch(() => null),
          qofeApi.get(dealId).catch(() => null),
        ]);
        if (!mounted) return;
        setDeal(dRes?.data || null);
        // qofe endpoint returns { status, qofe: {...} } — unwrap so bridge/flags resolve.
        setQofe(qRes?.data?.qofe || null);
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [dealId]);

  const bridge = qofe?.bridge;
  const entrySource = entryEbitda === '' ? 'qofe_defensible' : 'manual';
  const badge = badgeForSource(entrySource, bridge);
  const banner = payerBanner(qofe?.flags);
  const runBlocked = banner && banner.severity === 'CRITICAL';

  const run = async () => {
    if (runBlocked) return;
    setBusy(true); setError(null);
    try {
      const body = { assumptions };
      if (entryEbitda !== '') body.entry_ebitda = Number(entryEbitda);
      if (asc842Enabled && operatingLeasePv !== '') body.operating_lease_pv = Number(operatingLeasePv);
      if (physicianRollover !== '') body.physician_rollover_equity = Number(physicianRollover);
      const res = await lboApi.run(dealId, body);
      setModel(res.data.model);
    } catch (e) {
      setError(e?.response?.data?.message || 'LBO run failed. Is the financials service running?');
    } finally { setBusy(false); }
  };

  const lock = async () => {
    if (!model) return;
    setBusy(true);
    try { const res = await lboApi.lock(dealId, model.model_id); setModel(res.data.model); }
    catch (e) { setError('Lock failed (permission?).'); }
    finally { setBusy(false); }
  };

  const exportCsv = async () => {
    if (!model) return;
    setBusy(true); setError(null);
    try {
      const res = await exportApi.lbo(dealId, model.model_id, 'csv');
      const blob = new Blob([res.data.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.data.filename || 'lbo.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.status === 422 ? 'Export refused — missing provenance.' : 'Export failed.');
    } finally { setBusy(false); }
  };

  const r = model && model.results;

  return (
    <div style={{ padding: 16 }}>
      {/* Confidence badge */}
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
          padding: '3px 8px', borderRadius: 12, color: '#fff', background: badge.color,
        }}>{badge.text}</span>
        {bridge && (
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Reported {money(bridge.reported_ebitda)} → Adjusted {money(bridge.adjusted_ebitda)} → Defensible {money(bridge.defensible_ebitda)}
          </span>
        )}
      </div>

      {/* Payer banner */}
      {banner && (
        <div style={{
          marginBottom: 10, padding: '8px 10px', borderRadius: 8,
          background: banner.severity === 'CRITICAL' ? '#fef2f2' : '#fffbeb',
          border: `1px solid ${banner.color}`, color: banner.color, fontSize: 13, fontWeight: 500,
        }}>
          {banner.text}
          {runBlocked && <div style={{ fontSize: 11, marginTop: 4, fontWeight: 400 }}>Run blocked — resolve CRITICAL payer risk before modeling.</div>}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <label style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column' }}>
          Entry EBITDA (blank = QofE Defensible)
          <input value={entryEbitda} onChange={e => setEntryEbitda(e.target.value)} placeholder="auto"
            style={{ width: 130, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
        </label>

        <label style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column' }}>
          Physician rollover equity
          <input value={physicianRollover} onChange={e => setPhysicianRollover(e.target.value)} placeholder="0"
            style={{ width: 120, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
        </label>

        <label style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={asc842Enabled} onChange={e => setAsc842Enabled(e.target.checked)} />
            ASC 842 operating leases
          </span>
          {asc842Enabled && (
            <input value={operatingLeasePv} onChange={e => setOperatingLeasePv(e.target.value)} placeholder="PV of leases"
              style={{ width: 130, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
          )}
        </label>

        {FIELDS.map(([k, label]) => (
          <label key={k} style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column' }}>
            {label}
            <input type="number" step="any" value={assumptions[k]}
              onChange={e => setAssumptions({ ...assumptions, [k]: Number(e.target.value) })}
              style={{ width: 90, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </label>
        ))}
        <button onClick={run} disabled={busy || runBlocked}
          style={{ background: runBlocked ? '#94a3b8' : ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: runBlocked ? 'default' : 'pointer' }}>
          {busy ? 'Running…' : 'Run LBO'}
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c', marginTop: 10 }}>{error}</div>}

      {r && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <Stat label="MOIC" value={r.returns.moic ? `${Number(r.returns.moic).toFixed(2)}×` : '—'} />
            <Stat label="IRR" value={pctv(r.returns.irr)} />
            <Stat label="Entry equity" value={money(r.sources_uses.equity_entry)} />
            <Stat label="Exit equity" value={money(r.exit.equity)} />
            {r.sources_uses.physician_rollover_equity > 0 && (
              <Stat label="Rollover" value={money(r.sources_uses.physician_rollover_equity)} />
            )}
            {r.sources_uses.operating_lease_pv > 0 && (
              <Stat label="Lease PV" value={money(r.sources_uses.operating_lease_pv)} />
            )}
            <button onClick={exportCsv} disabled={busy}
              style={{ marginLeft: 'auto', background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
              Export CSV
            </button>
            <button onClick={lock} disabled={busy || model.is_locked}
              style={{ background: model.is_locked ? '#94a3b8' : '#fff', color: model.is_locked ? '#fff' : ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 8, padding: '6px 14px', cursor: model.is_locked ? 'default' : 'pointer' }}>
              {model.is_locked ? 'Locked ✓' : 'Lock model'}
            </button>
          </div>

          <h4 style={{ margin: '16px 0 6px' }}>Value-creation attribution</h4>
          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {Object.entries(r.attribution).map(([k, v]) => (
                <tr key={k} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '4px 10px', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '4px 10px', textAlign: 'right' }}>{money(v)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 600 }}>
                <td style={{ padding: '4px 10px' }}>Total value creation</td>
                <td style={{ padding: '4px 10px', textAlign: 'right' }}>{money(r.total_value_creation)}</td>
              </tr>
            </tbody>
          </table>

          <h4 style={{ margin: '16px 0 6px' }}>Operating &amp; debt schedule</h4>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
            <thead>
              <tr style={{ textAlign: 'right', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left' }}>Yr</th>
                <th style={{ padding: '4px 8px' }}>EBITDA</th><th style={{ padding: '4px 8px' }}>Interest</th>
                <th style={{ padding: '4px 8px' }}>FCF</th><th style={{ padding: '4px 8px' }}>Debt close</th>
              </tr>
            </thead>
            <tbody>
              {(r.periods || []).map(p => (
                <tr key={p.year} style={{ textAlign: 'right', borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '4px 8px', textAlign: 'left' }}>{p.year}</td>
                  <td style={{ padding: '4px 8px' }}>{num(p.ebitda)}</td>
                  <td style={{ padding: '4px 8px' }}>{num(p.interest)}</td>
                  <td style={{ padding: '4px 8px' }}>{num(p.fcf)}</td>
                  <td style={{ padding: '4px 8px' }}>{num(p.debt_close)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: ACCENT }}>{value}</div>
    </div>
  );
}
